import { GuidanceTooltip } from "@/components/guidance";
import { Avatar } from "@/components/ui/Avatar";
import { Brand } from "@/components/ui/Brand";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Skeleton } from "@/components/ui/Skeleton";
import { useTheme } from "@/components/ui/ThemeToggler/context";
import { THEME } from "@/components/ui/ThemeToggler/theme.types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/Tooltip/Tooltip";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import {
  Building2,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Cookie,
  FileSearch,
  FileText,
  Folder,
  FolderOpen,
  Gauge,
  GitBranch,
  Layers,
  LayoutDashboard,
  LogOut,
  MessageCirclePlus,
  Moon,
  PanelLeft,
  Plus,
  Search,
  Settings as SettingsIcon,
  Shield,
  Sun,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { type TCompanyResponse, type TCompanySectionThread } from "@/api/companies";
import { Input } from "@/components/ui/Input";
import {
  DEFAULT_SECTION_1,
  DEFAULT_SECTION_4,
  DEFAULT_SECTION_5,
  DEFAULT_SECTION_6,
  DEFAULT_SECTION_7,
} from "@/constants/sections";
import { useStore } from "@/store";
import { useChatStore } from "@/store/chat/chat.store";
import { isCompaniesEmpty } from "@/store/companies/companies.store";
import { hasAdminRole, hasReviewerRole, hasSalesRole } from "@/utils/routeGuards";
import { useAppLayout } from "../AppLayout/useAppLayout";

type TCompanySection = NonNullable<TCompanyResponse["sections"]>[number];

const buildCompanySectionKey = (companyId: string, sectionId?: string | null) =>
  `${companyId}:${sectionId ?? "default"}`;

function getSectionIcon(sectionId: string | null | undefined) {
  switch (sectionId) {
    case DEFAULT_SECTION_1:
      return <LayoutDashboard className="h-3.5 w-3.5" />;
    case DEFAULT_SECTION_4:
      return <Users className="h-3.5 w-3.5" />;
    case DEFAULT_SECTION_5:
      return <Layers className="h-3.5 w-3.5" />;
    case DEFAULT_SECTION_6:
      return <GitBranch className="h-3.5 w-3.5" />;
    case DEFAULT_SECTION_7:
      return <FileSearch className="h-3.5 w-3.5" />;
    default:
      return <Folder className="h-3.5 w-3.5" />;
  }
}

/** Derive active section context from current URL so sidebar state persists on refresh */
function useActiveRouteContext() {
  const location = useLocation();
  const params = useParams({ strict: false });
  const pathname = location.pathname;

  const companyId = params.company_id! ?? null;
  const sectionId = params.section_id! ?? null;
  const threadId = params.thread_id! ?? null;

  const isDashboard = sectionId != null && pathname.includes("/dashboard");
  const isNewChat = sectionId != null && pathname.endsWith("/new");
  const isSectionThread = sectionId != null && threadId != null && pathname.includes("/threads/");
  const isInvestorChat = sectionId != null && threadId != null && pathname.includes("/investor-chat/");

  return {
    companyId,
    sectionId,
    threadId,
    isDashboard,
    isNewChat,
    isSectionThread,
    isInvestorChat,
  };
}

type TSidebarProps = {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
};

export default function Sidebar({ isCollapsed = false, onToggleCollapse }: TSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const active = useActiveRouteContext();
  const {
    isOnboardingActive,
    openDashboard,
    closeDashboard,
    openChatThreadSession,
    threadTitles,
    getInvestorChatThreads,
  } = useChatStore(
    useShallow((state) => ({
      isOnboardingActive: state.isOnboardingActive,
      openDashboard: state.openDashboard,
      closeDashboard: state.closeDashboard,
      openChatThreadSession: state.openChatThreadSession,
      threadTitles: state.threadTitles,
      getInvestorChatThreads: state.getInvestorChatThreads,
    }))
  );
  const companies = useStore((state) => state.companies);
  const roles = useStore((state) => state.roles);
  const isAdmin = useMemo(() => hasAdminRole(roles), [roles]);
  const profile = useStore((state) => state.profile);
  const isLoadingCompanies = useStore((state) => state.isLoading);
  const companyError = useStore((state) => state.error);
  const fetchCompanies = useStore((state) => state.fetchCompanies);
  const { handleLogout } = useAppLayout();
  const { resolvedTheme, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [isProfileExpanded, setIsProfileExpanded] = useState(false);
  const [savedExpansionState, setSavedExpansionState] = useState<{
    companies: Record<string, boolean>;
    sections: Record<string, boolean>;
  } | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const profileSectionRef = useRef<HTMLDivElement>(null);
  const [expandedCompanies, setExpandedCompanies] = useState<Record<string, boolean>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // Fetch companies on mount (always fetch to ensure fresh data on page refresh)
  useEffect(() => {
    void fetchCompanies({ force: true });
  }, [fetchCompanies]);

  // Debounce search input by 150ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 150);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Initialize expanded state when companies data is available; prefer current URL so active state persists on refresh
  useEffect(() => {
    if (!companies || companies.length === 0) {
      return;
    }

    // If we're on a section route, expand only the active company and section from URL (so it persists on refresh)
    if (active.companyId && active.sectionId) {
      setExpandedCompanies({ [active.companyId]: true });
      setExpandedSections({
        [buildCompanySectionKey(active.companyId, active.sectionId)]: true,
      });
      return;
    }

    // Fallback: sort by created_at and expand first company + first section
    const sortedCompanies = [...companies].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });
    const completedCompanies = sortedCompanies.filter((company) => company.onboarding_completed);
    const allCompanies = sortedCompanies;
    const firstCompanyToExpand = completedCompanies.length > 0 ? completedCompanies[0] : allCompanies[0];
    if (firstCompanyToExpand) {
      setExpandedCompanies((prev) => ({ ...prev, [firstCompanyToExpand.id]: true }));
      const sortedSections = Array.isArray(firstCompanyToExpand.sections)
        ? [...firstCompanyToExpand.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        : [];
      if (sortedSections.length > 0) {
        const firstSection = sortedSections[0];
        setExpandedSections((prev) => ({
          ...prev,
          [buildCompanySectionKey(firstCompanyToExpand.id, firstSection.id)]: true,
        }));
      }
    }
  }, [companies, active.companyId, active.sectionId]);

  useEffect(() => {
    if (!isProfileExpanded) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (profileSectionRef.current && !profileSectionRef.current.contains(event.target as Node)) {
        setIsProfileExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isProfileExpanded]);

  const handleDashboardOpen = (company: TCompanyResponse, section?: TCompanySection) => {
    // If onboarding is not complete, redirect to onboarding
    if (!company.onboarding_completed) {
      void navigate({
        to: "/app/companies/$company_id/onboarding",
        params: { company_id: company.id },
      });
      return;
    }

    const sectionId = section?.id;
    if (sectionId) {
      // Navigate to section dashboard
      void navigate({
        to: "/app/companies/$company_id/sections/$section_id/dashboard",
        params: {
          company_id: company.id,
          section_id: sectionId,
        },
      });
    } else {
      // Fallback: open dashboard in store (for backward compatibility)
      openDashboard({
        companyId: company.id,
        companyName: company.name,
        sectionId: null,
        sectionName: company.name ?? "Dashboard",
        description: null,
      });
    }
  };

  const handleThreadSelect = (
    company: TCompanyResponse,
    section: TCompanySection | undefined,
    thread: TCompanySectionThread
  ) => {
    // If onboarding is not complete, redirect to onboarding
    if (!company.onboarding_completed) {
      void navigate({
        to: "/app/companies/$company_id/onboarding",
        params: { company_id: company.id },
      });
      return;
    }

    closeDashboard();
    openChatThreadSession({
      threadId: thread.id,
      companyId: company.id,
      companyName: company.name,
      sectionId: section?.id ?? null,
      sectionName: section?.name ?? company.name ?? "Thread",
      title: thread.title ?? "Chat Thread",
      initialPrompt: null,
    });

    // Navigate to the thread route
    if (section?.id) {
      void navigate({
        to: "/app/companies/$company_id/sections/$section_id/threads/$thread_id",
        params: {
          company_id: company.id,
          section_id: section.id,
          thread_id: thread.id,
        },
      });
    }
  };

  const handleInvestorThreadSelect = (
    company: TCompanyResponse,
    section: TCompanySection | undefined,
    thread: { id: string; title: string; founder_company_id: string; founder_name: string }
  ) => {
    if (!company.onboarding_completed) {
      void navigate({
        to: "/app/companies/$company_id/onboarding",
        params: { company_id: company.id },
      });
      return;
    }
    closeDashboard();
    if (section?.id) {
      void navigate({
        to: "/app/companies/$company_id/sections/$section_id/investor-chat/$thread_id",
        params: {
          company_id: company.id,
          section_id: section.id,
          thread_id: thread.id,
        },
        search: {
          founder_company_id: thread.founder_company_id,
          founder_name: thread.founder_name,
          initial_prompt: "",
        },
      });
    }
  };

  const toggleSectionExpansion = (companyId: string, sectionId?: string | null) => {
    const key = buildCompanySectionKey(companyId, sectionId);
    setExpandedSections((prev) => {
      const isCurrentlyExpanded = prev[key] ?? false;
      // If clicking to expand, collapse ALL sections first (only one section open at a time)
      if (!isCurrentlyExpanded) {
        const newState: Record<string, boolean> = {};
        // Expand only the clicked section
        newState[key] = true;
        return newState;
      }
      // If clicking to collapse, just collapse this one
      return {
        ...prev,
        [key]: false,
      };
    });
  };

  const [showAddCompanyConfirm, setShowAddCompanyConfirm] = useState(false);

  const handleStartOnboarding = () => {
    const hasCompletedCompany = companies?.some((c) => c.onboarding_completed);
    if (hasCompletedCompany) {
      setShowAddCompanyConfirm(true);
      return;
    }
    proceedWithOnboarding();
  };

  const proceedWithOnboarding = () => {
    setShowAddCompanyConfirm(false);
    const incompleteCompany = companies?.find((c) => !c.onboarding_completed);
    if (incompleteCompany) {
      void navigate({
        to: "/app/companies/$company_id/onboarding",
        params: { company_id: incompleteCompany.id },
      });
      return;
    }
    // `new: true` bypasses the first-time-onboarding guard so existing users
    // can deliberately add another company.
    void navigate({ to: "/app/onboarding", search: { new: true } });
  };

  const handleImproveExistingProfile = () => {
    setShowAddCompanyConfirm(false);
    const targetCompany =
      companies?.find((c) => c.id === active.companyId && c.onboarding_completed) ??
      companies?.find((c) => c.onboarding_completed);
    if (!targetCompany) return;
    const firstSection = Array.isArray(targetCompany.sections)
      ? [...targetCompany.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0]
      : undefined;
    if (firstSection) {
      void navigate({
        to: "/app/companies/$company_id/sections/$section_id/dashboard",
        params: { company_id: targetCompany.id, section_id: firstSection.id },
      });
    }
  };

  const handleSearchClick = () => {
    searchInputRef.current?.focus();
  };

  const handleSearchChange = (value: string) => {
    if (value && !searchQuery) {
      setSavedExpansionState({
        companies: { ...expandedCompanies },
        sections: { ...expandedSections },
      });
    }
    setSearchQuery(value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    if (savedExpansionState) {
      setExpandedCompanies(savedExpansionState.companies);
      setExpandedSections(savedExpansionState.sections);
      setSavedExpansionState(null);
    }
  };

  // Company Knowledge nav - commented out
  // const handleCompanyKnowledge = (companyId: string, companyName?: string | null) => {
  //   const company = companies?.find((c) => c.id === companyId);
  //   if (company && !company.onboarding_completed) {
  //     void navigate({
  //       to: "/app/companies/$company_id/onboarding",
  //       params: { company_id: companyId },
  //     });
  //     return;
  //   }
  //   openCompanyKnowledge({ companyId, companyName: companyName ?? null });
  // };

  const toggleCompanyExpansion = (companyId: string) => {
    setExpandedCompanies((prev) => {
      const isCurrentlyExpanded = prev[companyId] ?? false;

      // If clicking to expand, collapse all other companies and expand first section
      if (!isCurrentlyExpanded) {
        const newState: Record<string, boolean> = {};
        newState[companyId] = true;
        // Find the company and expand its first section
        const company = companies && companies.length > 0 ? companies.find((c) => c.id === companyId) : undefined;
        if (company) {
          const sortedSections = Array.isArray(company.sections)
            ? [...company.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            : [];
          if (sortedSections.length > 0) {
            const firstSection = sortedSections[0];
            const sectionKey = buildCompanySectionKey(companyId, firstSection.id);
            setExpandedSections(() => {
              // Collapse all sections first (only one section open at a time)
              const newSectionState: Record<string, boolean> = {};
              // Expand the first section of the newly expanded company
              newSectionState[sectionKey] = true;
              return newSectionState;
            });
          }
        }
        return newState;
      }
      // If clicking to collapse, just collapse this one
      return {
        ...prev,
        [companyId]: false,
      };
    });
  };

  const handleNewChat = (company: TCompanyResponse, section?: TCompanySection) => {
    // If onboarding is not complete, redirect to onboarding
    if (!company.onboarding_completed) {
      void navigate({
        to: "/app/companies/$company_id/onboarding",
        params: { company_id: company.id },
      });
      return;
    }

    // Use the provided section or find the General section by fixed ID
    const targetSection =
      section ??
      (Array.isArray(company.sections)
        ? (company.sections.find((s) => s.id === DEFAULT_SECTION_1) ?? company.sections[0])
        : null);
    if (targetSection) {
      const sectionId = targetSection.id || targetSection.name || null;
      closeDashboard();
      // Navigate to new chat page - let the page component handle session setup
      if (sectionId) {
        void navigate({
          to: "/app/companies/$company_id/sections/$section_id/new",
          params: {
            company_id: company.id,
            section_id: sectionId,
          },
          replace: true,
        });
      }
    }
  };

  /**
   * Search functionality:
   * 1. Filters companies by matching company name
   * 2. Filters threads by matching thread title
   * 3. When a thread matches, its parent company and section are shown
   * 4. Auto-expands companies and sections that contain matching threads
   */
  const normalizeSearchQuery = (query: string) => query.trim().toLowerCase();
  const searchQueryNormalized = normalizeSearchQuery(debouncedSearchQuery);

  // Filter and process companies based on search using useMemo for performance
  const companyList = useMemo(() => {
    const allCompanies = isCompaniesEmpty(companies) ? [] : (companies ?? []);

    // Sort companies by created_at in descending order (newest first)
    const sortedCompanies = [...allCompanies].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });

    if (!searchQueryNormalized) {
      return sortedCompanies;
    }

    // Helper functions for search matching
    const matchesSearch = (text: string | null | undefined) => {
      if (!text || !searchQueryNormalized) return false;
      return text.toLowerCase().includes(searchQueryNormalized);
    };

    // Check if a thread matches the search query
    const threadMatchesSearch = (thread: TCompanySectionThread) => {
      if (!searchQueryNormalized) return true;
      const threadTitle = threadTitles[thread.id] ?? thread?.title?.trim() ?? thread?.name?.trim() ?? "Untitled Chat";
      return matchesSearch(threadTitle);
    };

    // Check if a section has any matching threads (including investor threads for deal pipeline)
    const sectionHasMatchingThreads = (company: TCompanyResponse, section: TCompanySection) => {
      if (!searchQueryNormalized) return true;
      const threads: TCompanySectionThread[] = Array.isArray(section.threads) ? section.threads : [];
      const hasMatchingServerThreads = threads.some(threadMatchesSearch);
      if (hasMatchingServerThreads) return true;
      if (section.id === DEFAULT_SECTION_6) {
        const investorThreads = getInvestorChatThreads(company.id, section.id);
        return investorThreads.some((t) => matchesSearch(t.title));
      }
      return false;
    };

    // Check if a company matches search (by name or has matching threads)
    const companyMatchesSearch = (company: TCompanyResponse) => {
      if (!searchQueryNormalized) return true;

      // Check if company name matches
      if (matchesSearch(company.name)) return true;

      // Check if any section has matching threads
      const sections = Array.isArray(company.sections) ? company.sections : [];
      return sections.some((section) => sectionHasMatchingThreads(company, section));
    };

    return sortedCompanies.filter(companyMatchesSearch).map((company) => {
      // For each company, filter its sections to only show those with matches
      const sections = Array.isArray(company.sections) ? company.sections : [];
      const filteredSections = sections.filter((section) => {
        // Include section if it matches by name or has matching threads
        if (matchesSearch(section.name)) return true;
        return sectionHasMatchingThreads(company, section);
      });

      // Return company with filtered sections
      return {
        ...company,
        sections: filteredSections,
      };
    });
  }, [companies, searchQueryNormalized, threadTitles, getInvestorChatThreads]);

  // Auto-expand companies and sections when searching
  useEffect(() => {
    if (!searchQueryNormalized || !companies || companies.length === 0) {
      return;
    }

    const matchesSearch = (text: string | null | undefined) => {
      if (!text || !searchQueryNormalized) return false;
      return text.toLowerCase().includes(searchQueryNormalized);
    };

    const threadMatchesSearch = (thread: TCompanySectionThread) => {
      if (!searchQueryNormalized) return true;
      const threadTitle = threadTitles[thread.id] ?? thread?.title?.trim() ?? thread?.name?.trim() ?? "Untitled Chat";
      return matchesSearch(threadTitle);
    };

    const sectionHasMatchingThreadsForExpand = (company: TCompanyResponse, section: TCompanySection) => {
      if (!searchQueryNormalized) return true;
      const threads: TCompanySectionThread[] = Array.isArray(section.threads) ? section.threads : [];
      if (threads.some(threadMatchesSearch)) return true;
      if (section.id === DEFAULT_SECTION_6) {
        const investorThreads = getInvestorChatThreads(company.id, section.id);
        return investorThreads.some((t) => matchesSearch(t.title));
      }
      return false;
    };

    const newExpandedCompanies: Record<string, boolean> = {};
    const newExpandedSections: Record<string, boolean> = {};

    companyList.forEach((company) => {
      // Expand company if it has matching threads or name matches
      const sections = Array.isArray(company.sections) ? company.sections : [];
      const hasMatchingThreads = sections.some((section) => sectionHasMatchingThreadsForExpand(company, section));

      if (hasMatchingThreads || matchesSearch(company.name)) {
        newExpandedCompanies[company.id] = true;

        // Expand sections that have matching threads
        sections.forEach((section) => {
          if (sectionHasMatchingThreadsForExpand(company, section) || matchesSearch(section.name)) {
            const sectionKey = buildCompanySectionKey(company.id, section.id);
            newExpandedSections[sectionKey] = true;
          }
        });
      }
    });

    setExpandedCompanies(newExpandedCompanies);
    setExpandedSections(newExpandedSections);
  }, [searchQueryNormalized, companies, companyList, threadTitles, getInvestorChatThreads]);

  const addCompanyConfirmDialog = (
    <Dialog
      open={showAddCompanyConfirm}
      onOpenChange={setShowAddCompanyConfirm}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add another company?</DialogTitle>
          <DialogDescription>
            Investors on Fundy discover companies with complete, optimised profiles. Strengthening an existing profile
            could improve your funding chances or go ahead and create a new one.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={proceedWithOnboarding}
          >
            Add new company
          </Button>
          <Button
            className="w-full sm:w-auto"
            onClick={handleImproveExistingProfile}
          >
            Improve existing profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (isCollapsed) {
    return (
      <>
        <TooltipProvider delayDuration={300}>
          <div className="flex h-full w-full flex-col items-center gap-2 overflow-y-auto rounded-2xl bg-card py-3 shadow-lg">
            {/* Brand mark + expand toggle */}
            <Brand
              to="/app"
              compact
              className="mb-1"
            />
            {onToggleCollapse && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onToggleCollapse}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-foreground transition-colors hover:bg-accent"
                    aria-label="Expand sidebar"
                  >
                    <PanelLeft className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Expand sidebar</TooltipContent>
              </Tooltip>
            )}
            <div className="h-px w-6 bg-border" />
            {/* Top row: search + add — mirrors expanded sidebar */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleSearchClick}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-muted transition-colors hover:bg-accent"
                  aria-label="Search"
                >
                  <Search className="h-4 w-4 text-muted-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Filter companies and chats</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleStartOnboarding}
                  disabled={isOnboardingActive}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-primary transition-colors hover:bg-primary/90"
                  aria-label="Add a new company"
                >
                  <Plus className="h-4 w-4 text-primary-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Add a new company</TooltipContent>
            </Tooltip>

            <div className="my-1 h-px w-6 bg-border" />

            {/* Company icons — section mini-icons only for the active company */}
            {isLoadingCompanies ? (
              <>
                <Skeleton className="h-9 w-9 rounded-full" />
                <Skeleton className="h-9 w-9 rounded-full" />
              </>
            ) : (
              companyList.map((company) => {
                const isActiveCompany = active.companyId === company.id;
                const sectionList = Array.isArray(company.sections)
                  ? [...company.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  : [];
                const activeSection = sectionList.find((s) => s.id === active.sectionId) ?? sectionList[0];

                return (
                  <div
                    key={company.id}
                    className="flex w-full flex-col items-center gap-1"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => handleDashboardOpen(company, activeSection)}
                          className={`relative flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                            isActiveCompany
                              ? "bg-primary text-primary-foreground hover:bg-primary/90"
                              : "bg-muted hover:bg-accent"
                          }`}
                          aria-label={company.name ?? company.id}
                        >
                          <Building2 className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p className="font-medium">{company.name ?? company.id}</p>
                        {activeSection && !isActiveCompany && (
                          <p className="text-xs text-muted-foreground">{activeSection.name}</p>
                        )}
                      </TooltipContent>
                    </Tooltip>

                    {/* Section icons + new chat — only shown under the active company */}
                    {isActiveCompany && sectionList.length > 0 && (
                      <div className="flex flex-col items-center gap-1">
                        {sectionList.map((section) => {
                          const sectionId = section.id ?? section.name ?? null;
                          const isActiveSection = active.sectionId === sectionId;

                          return (
                            <Tooltip key={sectionId ?? section.name}>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() => handleDashboardOpen(company, section)}
                                  className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${
                                    isActiveSection
                                      ? "bg-primary/20 text-primary"
                                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                  }`}
                                  aria-label={section.name}
                                >
                                  {getSectionIcon(sectionId)}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                <p className="font-medium">{section.name}</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}

                        {/* New chat shortcut */}
                        <div className="my-0.5 h-px w-4 bg-border/50" />
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => {
                                const activeSection =
                                  sectionList.find((s) => s.id === active.sectionId) ?? sectionList[0];
                                handleNewChat(company, activeSection);
                              }}
                              className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                              aria-label="New chat"
                            >
                              <MessageCirclePlus className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p className="font-medium">New chat</p>
                            <p className="text-xs text-muted-foreground">
                              {(sectionList.find((s) => s.id === active.sectionId) ?? sectionList[0])?.name}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                );
              })
            )}

            <div className="mt-auto flex flex-col items-center gap-2">
              {hasReviewerRole(roles) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => void navigate({ to: "/app/reviews" })}
                      className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                        location.pathname.startsWith("/app/reviews")
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-accent"
                      }`}
                      aria-label="Reviews"
                    >
                      <ClipboardCheck className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Reviews</TooltipContent>
                </Tooltip>
              )}
              {hasSalesRole(roles) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => void navigate({ to: "/app/sales/onboarding-jobs" })}
                      className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                        location.pathname.startsWith("/app/sales")
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-accent"
                      }`}
                      aria-label="Investor Onboarding"
                    >
                      <UserCheck className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Investor Onboarding</TooltipContent>
                </Tooltip>
              )}
              {isAdmin && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => void navigate({ to: "/app/admin/analytics" })}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors hover:bg-primary/20"
                      aria-label="Admin Dashboard"
                    >
                      <Shield className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Admin Dashboard</TooltipContent>
                </Tooltip>
              )}
              <div className="h-px w-6 bg-border" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => void navigate({ to: "/app/settings" })}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-muted transition-colors hover:bg-accent"
                    aria-label="Settings & Account"
                  >
                    <Avatar
                      src={undefined}
                      alt={profile?.first_name ?? "User"}
                      name={profile?.first_name ?? undefined}
                      size="sm"
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Account &amp; Settings</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => void handleLogout()}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-accent hover:text-destructive"
                    aria-label="Logout"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Logout</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </TooltipProvider>
        {addCompanyConfirmDialog}
      </>
    );
  }

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl bg-card p-2 shadow-lg md:p-3 lg:p-4">
          {/* Brand + collapse toggle */}
          <div className="mb-3 flex items-center justify-between px-1">
            <Brand to="/app" />
            {onToggleCollapse && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onToggleCollapse}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    aria-label="Collapse sidebar"
                  >
                    <PanelLeft className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Collapse sidebar</TooltipContent>
              </Tooltip>
            )}
          </div>
          {/* Search Bar with Icon Buttons */}
          <div className="mb-2 flex items-center gap-1.5 md:mb-3">
            <button
              onClick={handleSearchClick}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted transition-colors hover:bg-accent md:h-10 md:w-10"
              aria-label="Search"
              data-tour="sidebar-search"
            >
              <Search className="h-3.5 w-3.5 text-muted-foreground md:h-4 md:w-4" />
            </button>
            <div className="relative flex-1">
              <Input
                ref={searchInputRef}
                placeholder="Filter chats…"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full rounded-lg bg-muted pr-7 text-xs md:text-sm"
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
                  aria-label="Clear filter"
                >
                  <X
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                </button>
              )}
            </div>
            <button
              onClick={handleStartOnboarding}
              disabled={isOnboardingActive}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary transition-colors hover:bg-primary/90 md:h-10 md:w-10"
              aria-label="Add a new company"
              data-tour="sidebar-add-company"
            >
              <Plus className="h-3.5 w-3.5 text-primary-foreground md:h-4 md:w-4" />
            </button>
          </div>

          <div className="scrollbar-hide flex-1 space-y-2 overflow-y-auto">
            {isLoadingCompanies && (
              <div className="space-y-3 px-1 py-2">
                {/* Company 1 — expanded */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-1.5">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3.5 w-3.5 rounded" />
                  </div>
                  <div className="space-y-3 pl-3">
                    {[1, 2].map((s) => (
                      <div
                        key={s}
                        className="space-y-1.5"
                      >
                        <Skeleton className="h-3 w-20" />
                        <div className="space-y-1 pl-2">
                          <Skeleton className="h-3 w-32" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Company 2 — collapsed */}
                <div className="flex items-center justify-between py-1.5">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-3.5 w-3.5 rounded" />
                </div>
              </div>
            )}
            {companyError && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                {companyError}
              </div>
            )}
            {!isLoadingCompanies && !companyError && (isCompaniesEmpty(companies) || companyList.length === 0) && (
              <div className="rounded-lg border border-dashed border-border p-2 text-xs text-muted-foreground">
                {searchQueryNormalized
                  ? `No companies or threads found matching "${searchQuery}"`
                  : "No companies yet. Click the + button to add a new company."}
              </div>
            )}
            {companyList.map((company, index) => {
              const isExpanded = expandedCompanies[company.id] ?? false; // Default to collapsed
              const sectionList = Array.isArray(company.sections)
                ? [...company.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                : [];

              return (
                <div
                  key={company.id}
                  className="w-full space-y-2"
                >
                  {/* Company Section - Only shows Company Knowledge and Onboarding Summary */}
                  <div className="rounded-lg bg-card">
                    <button
                      type="button"
                      onClick={() => toggleCompanyExpansion(company.id)}
                      className="flex w-full items-center gap-2 rounded-lg py-1.5 text-left text-sm font-bold text-foreground transition-colors hover:bg-accent md:text-base"
                      data-tour={index === 0 ? "sidebar-company-selector" : undefined}
                    >
                      <Building2 className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground md:h-4 md:w-4" />
                      <span className="min-w-0 flex-1 truncate">{company.name ?? company.id}</span>
                      {isExpanded ? (
                        <ChevronUp className="h-3.5 w-3.5 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="pl-3">
                        <div className="space-y-0.5 pb-2">
                          {/* Company Knowledge - commented out
                      <button
                        type="button"
                        className={`flex w-full items-center gap-1.5 rounded-lg py-1 text-left text-xs font-normal text-gray-900 hover:bg-gray-50 md:text-sm ${
                          isCompanyKnowledgeOpen && companyKnowledgeContext?.companyId === company.id
                            ? "bg-blue-50 text-blue-700"
                            : ""
                        }`}
                        onClick={() => handleCompanyKnowledge(company.id, company.name)}
                      >
                        <Brain className="h-3.5 w-3.5 flex-shrink-0 text-blue-600 md:h-4 md:w-4" />
                        Company Knowledge
                      </button>
                      */}
                        </div>

                        {/* Separator after company items */}
                        <div className="border-t border-border pb-2"></div>

                        {/* All Sections - Each as a separate top-level section */}
                        <div className="space-y-3 pb-2">
                          {sectionList.map((section, sectionIndex) => {
                            const sectionId = section.id ?? section.name ?? null;
                            const sectionKey = buildCompanySectionKey(company.id, sectionId);
                            const isSectionExpanded = expandedSections[sectionKey] ?? false;
                            const serverThreads: TCompanySectionThread[] = Array.isArray(section.threads)
                              ? section.threads
                              : [];
                            const isDealPipeline = sectionId === DEFAULT_SECTION_6;
                            const investorThreads = isDealPipeline
                              ? getInvestorChatThreads(company.id, sectionId ?? "")
                              : [];

                            return (
                              <div
                                key={sectionKey}
                                className="space-y-2"
                              >
                                {sectionIndex > 0 && <div className="border-t border-border"></div>}
                                <div
                                  className="rounded-lg bg-card"
                                  data-tour={sectionIndex === 0 && index === 0 ? "sidebar-section-list" : undefined}
                                >
                                  <button
                                    type="button"
                                    onClick={() => toggleSectionExpansion(company.id, sectionId)}
                                    className="flex w-full items-center gap-2 rounded-lg py-1.5 text-left text-xs font-semibold text-foreground transition-colors hover:bg-accent md:text-sm"
                                  >
                                    {isSectionExpanded ? (
                                      <FolderOpen className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                                    ) : (
                                      <Folder className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                                    )}
                                    <span className="min-w-0 flex-1 truncate">{section.name}</span>
                                    {isSectionExpanded ? (
                                      <ChevronUp className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                                    ) : (
                                      <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                                    )}
                                  </button>
                                  {isSectionExpanded && (
                                    <div className="space-y-0 pb-2">
                                      {/* New Chat Button */}
                                      <GuidanceTooltip
                                        guidanceId="sidebar-new-chat-v1"
                                        trigger="firstInteraction"
                                      >
                                        <button
                                          type="button"
                                          className={`flex w-full items-center gap-1.5 rounded-lg px-2 py-1 text-left text-xs font-normal hover:bg-accent md:text-sm ${
                                            active.companyId === company.id &&
                                            active.sectionId === sectionId &&
                                            active.isNewChat
                                              ? "bg-primary/10 text-primary"
                                              : "text-foreground"
                                          }`}
                                          onClick={() => handleNewChat(company, section)}
                                          data-tour={sectionIndex === 0 && index === 0 ? "sidebar-new-chat" : undefined}
                                        >
                                          <MessageCirclePlus className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground md:h-4 md:w-4" />
                                          New Chat
                                        </button>
                                      </GuidanceTooltip>
                                      {/* Dashboard Button */}
                                      <button
                                        type="button"
                                        className={`flex w-full items-center gap-1.5 rounded-lg px-2 py-1 text-left text-xs font-normal hover:bg-accent md:text-sm ${
                                          active.companyId === company.id &&
                                          active.sectionId === sectionId &&
                                          active.isDashboard
                                            ? "bg-primary/10 text-primary"
                                            : "text-foreground"
                                        }`}
                                        onClick={() => handleDashboardOpen(company, section)}
                                      >
                                        <Gauge className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground md:h-4 md:w-4" />
                                        Dashboard
                                      </button>
                                      {/* Threads List */}
                                      <div className="space-y-0.5 px-2 py-1">
                                        {(serverThreads.length > 0 || investorThreads.length > 0) && (
                                          <p className="pb-0.5 pt-2 text-xs font-medium text-muted-foreground">
                                            Recents
                                          </p>
                                        )}
                                        {(() => {
                                          const filterThread = (
                                            thread: { id: string; title?: string | null; name?: string | null },
                                            getTitle: (t: typeof thread) => string
                                          ) => {
                                            if (!searchQueryNormalized) return true;
                                            const title = getTitle(thread);
                                            return title.toLowerCase().includes(searchQueryNormalized);
                                          };
                                          const filteredServerThreads = serverThreads.filter((t) =>
                                            filterThread(
                                              t,
                                              (x) =>
                                                threadTitles[x.id] ??
                                                x?.title?.trim() ??
                                                x?.name?.trim() ??
                                                "Untitled Chat"
                                            )
                                          );
                                          const filteredInvestorThreads = investorThreads.filter((t) =>
                                            filterThread(t, (x) => String(x.title ?? ""))
                                          );
                                          const hasServerThreads = filteredServerThreads.length > 0;
                                          const hasInvestorThreads = filteredInvestorThreads.length > 0;

                                          if (!hasServerThreads && !hasInvestorThreads) {
                                            return searchQueryNormalized ? (
                                              <p className="text-xs text-muted-foreground">No matching chats found.</p>
                                            ) : (
                                              <p className="text-xs text-muted-foreground">No chats yet.</p>
                                            );
                                          }

                                          return (
                                            <>
                                              {filteredServerThreads.map((thread) => {
                                                const threadTitle =
                                                  threadTitles[thread.id] ??
                                                  thread?.title?.trim() ??
                                                  thread?.name?.trim() ??
                                                  "Untitled Chat";
                                                const isActiveThread =
                                                  active.companyId === company.id &&
                                                  active.sectionId === sectionId &&
                                                  active.isSectionThread &&
                                                  active.threadId === thread.id;
                                                return (
                                                  <button
                                                    key={`thread-${thread.id}`}
                                                    type="button"
                                                    className={`w-full rounded-md py-0.5 text-left text-xs hover:bg-accent md:text-sm ${
                                                      isActiveThread
                                                        ? "bg-accent font-medium text-foreground"
                                                        : "text-foreground"
                                                    }`}
                                                    onClick={() => handleThreadSelect(company, section, thread)}
                                                  >
                                                    {threadTitle}
                                                  </button>
                                                );
                                              })}
                                              {filteredInvestorThreads.map((thread) => {
                                                const isActiveInvestorThread =
                                                  active.companyId === company.id &&
                                                  active.sectionId === sectionId &&
                                                  active.isInvestorChat &&
                                                  active.threadId === thread.id;
                                                return (
                                                  <button
                                                    key={`investor-${thread.id}`}
                                                    type="button"
                                                    className={`w-full rounded-md py-0.5 text-left text-xs hover:bg-accent md:text-sm ${
                                                      isActiveInvestorThread
                                                        ? "bg-accent font-medium text-foreground"
                                                        : "text-foreground"
                                                    }`}
                                                    onClick={() => handleInvestorThreadSelect(company, section, thread)}
                                                  >
                                                    {thread.title}
                                                  </button>
                                                );
                                              })}
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {index < companyList.length - 1 && <div className="my-2" />}
                </div>
              );
            })}
          </div>
          {hasReviewerRole(roles) && (
            <div className="mt-3 border-t border-border pt-3">
              <button
                type="button"
                onClick={() => {
                  void navigate({ to: "/app/reviews" });
                }}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors ${
                  location.pathname.startsWith("/app/reviews")
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-accent"
                }`}
              >
                <ClipboardCheck className="h-4 w-4" />
                <span>Reviews</span>
              </button>
            </div>
          )}
          {hasSalesRole(roles) && (
            <div className="mt-3 border-t border-border pt-3">
              <button
                type="button"
                onClick={() => {
                  void navigate({ to: "/app/sales/onboarding-jobs" });
                }}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors ${
                  location.pathname.startsWith("/app/sales")
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-accent"
                }`}
              >
                <UserCheck className="h-4 w-4" />
                <span>Investor Onboarding</span>
              </button>
            </div>
          )}

          {/* Pinned account section */}
          <div
            ref={profileSectionRef}
            className="mt-2 border-t border-border pt-2"
          >
            {isProfileExpanded && (
              <div className="mb-1 space-y-0.5 rounded-lg border border-border/50 p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => void navigate({ to: "/app/settings" })}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
                >
                  <SettingsIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                  <span>Settings</span>
                </button>
                <button
                  type="button"
                  onClick={() => toggleTheme()}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
                >
                  {resolvedTheme === THEME.light ? (
                    <Moon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                  ) : (
                    <Sun className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                  )}
                  <span>{resolvedTheme === THEME.light ? "Dark Mode" : "Light Mode"}</span>
                </button>
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <FileText className="h-3 w-3 flex-shrink-0" />
                  Terms &amp; Conditions
                </a>
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <Shield className="h-3 w-3 flex-shrink-0" />
                  Privacy Policy
                </a>
                <a
                  href="/cookies-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <Cookie className="h-3 w-3 flex-shrink-0" />
                  Cookies Policy
                </a>
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-destructive"
                >
                  <LogOut className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>Logout</span>
                </button>
                <div className="my-1 border-t border-border" />
              </div>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => void navigate({ to: "/app/admin/analytics" })}
                className="mb-1 flex w-full items-center gap-2 rounded-lg border border-primary/20 bg-primary/10 px-2 py-1.5 text-left transition-colors hover:bg-primary/15"
              >
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-primary/20">
                  <Shield className="h-3.5 w-3.5 text-primary" />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-primary">Admin Dashboard</span>
                <span className="rounded bg-primary/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                  Admin
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsProfileExpanded((v) => !v)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent"
            >
              <Avatar
                src={undefined}
                alt={profile?.first_name ?? "User"}
                name={profile?.first_name ?? undefined}
                size="sm"
                className="flex-shrink-0"
              />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {profile?.first_name?.trim() ? profile.first_name.trim() : (profile?.email ?? "Account")}
              </span>
              {isProfileExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
              ) : (
                <ChevronUp className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
              )}
            </button>
          </div>
        </div>
      </TooltipProvider>
      {addCompanyConfirmDialog}
    </>
  );
}
